import { CLIENT_OUTDATED_MESSAGE, CLOSE_CODES } from "@townsy/contracts";

/**
 * WebSocket close codes used by the Colyseus 0.18 SDK (`@colyseus/shared-types` 0.18.3). Copied as
 * constants so this module stays dependency-free and unit-testable in Node.
 */
export const SDK_CLOSE_CODES = {
  NORMAL_CLOSURE: 1000,
  GOING_AWAY: 1001,
  NO_STATUS_RECEIVED: 1005,
  ABNORMAL_CLOSURE: 1006,
  CONSENTED: 4000,
  SERVER_SHUTDOWN: 4001,
  WITH_ERROR: 4002,
  FAILED_TO_RECONNECT: 4003,
  MAY_TRY_RECONNECT: 4010,
} as const;

/** Matchmaking HTTP error codes from the SDK (`ErrorCode` in `@colyseus/shared-types`). */
export const SDK_MATCHMAKE_CODES = {
  MATCHMAKE_NO_HANDLER: 520,
  MATCHMAKE_INVALID_CRITERIA: 521,
  MATCHMAKE_INVALID_ROOM_ID: 522,
  MATCHMAKE_UNHANDLED: 523,
  MATCHMAKE_EXPIRED: 524,
  AUTH_FAILED: 525,
  APPLICATION_ERROR: 526,
} as const;

export type RoomCloseState =
  | "left"
  | "control_taken"
  | "kicked"
  | "banned"
  | "ticket_invalid"
  | "room_full"
  | "room_suspended"
  | "incompatible_client"
  | "server_restarting"
  | "disconnected"
  | "join_failed";

export interface CloseInfo {
  state: RoomCloseState;
  code: number;
  title: string;
  message: string;
  /** The user may retry (re-join) from the UI. */
  retryable: boolean;
  /** The client should re-join automatically (after a short delay) without asking. */
  autoRejoin: boolean;
  /** Nothing should be shown to the user (e.g. we left on purpose). */
  silent: boolean;
}

/** Map a room close code to the UI state it should produce (docs/03 sections 1, 4, 5; docs/06 section 10). */
export function describeClose(code: number, reason?: string): CloseInfo {
  const base = { code, retryable: true, autoRejoin: false, silent: false };
  switch (code) {
    case SDK_CLOSE_CODES.CONSENTED:
      return { ...base, state: "left", title: "Left the room", message: "", retryable: false, silent: true };
    case CLOSE_CODES.CONTROL_TAKEN:
      return {
        ...base,
        state: "control_taken",
        title: "Townsy is open in another tab",
        message: "Your avatar is now controlled from another tab or window. You can take control here instead.",
      };
    case CLOSE_CODES.KICKED:
      return {
        ...base,
        state: "kicked",
        title: "Removed from the room",
        message: reason || "A moderator removed you from this room.",
        retryable: false,
      };
    case CLOSE_CODES.BANNED:
      return {
        ...base,
        state: "banned",
        title: "Access restricted",
        message: reason || "This account is restricted from Townsy.",
        retryable: false,
      };
    case CLOSE_CODES.TICKET_INVALID:
      return {
        ...base,
        state: "ticket_invalid",
        title: "Room pass expired",
        message: "Your room pass was not accepted. Requesting a new one…",
        autoRejoin: true,
      };
    case CLOSE_CODES.ROOM_FULL:
      return {
        ...base,
        state: "room_full",
        title: "Room full",
        message: "This room is full right now. Wait a moment and try again.",
      };
    case CLOSE_CODES.ROOM_SUSPENDED:
      return {
        ...base,
        state: "room_suspended",
        title: "Room closed",
        message: reason || "This room is temporarily closed for safety review.",
        retryable: false,
      };
    case CLOSE_CODES.INCOMPATIBLE_CLIENT:
      // ADR-0036: only a reload helps (the banner offers a Reload button instead of Rejoin).
      return {
        ...base,
        state: "incompatible_client",
        title: "Update available",
        message: CLIENT_OUTDATED_MESSAGE,
        retryable: false,
      };
    case SDK_CLOSE_CODES.SERVER_SHUTDOWN:
      return {
        ...base,
        state: "server_restarting",
        title: "Townsy is restarting",
        message: "The town is restarting. Reconnecting you shortly…",
        autoRejoin: true,
      };
    // The connection dropped and the SDK's own reconnection could not restore the session (it gave up, or the server's
    // grace had ended): join the same room again with a new pass, without a click (founder 2026-10-28: "i keep getting
    // kicked out of the world in the game and have to restart").
    case SDK_CLOSE_CODES.FAILED_TO_RECONNECT:
    case SDK_CLOSE_CODES.ABNORMAL_CLOSURE:
      return {
        ...base,
        state: "disconnected",
        title: "Connection lost",
        message: "The connection dropped. Reconnecting you…",
        autoRejoin: true,
      };
    case SDK_CLOSE_CODES.WITH_ERROR:
      return {
        ...base,
        state: "disconnected",
        title: "Connection error",
        message: reason || "The room closed because of a server error.",
      };
    default:
      return {
        ...base,
        state: "disconnected",
        title: "Connection lost",
        message: reason || "The connection to the town was interrupted.",
      };
  }
}

export interface JoinFailure {
  state: RoomCloseState;
  message: string;
  retryable: boolean;
}

/** Normalise an error thrown by matchmaking / room join into a UI state. */
export function describeJoinError(err: unknown): JoinFailure {
  const e = (err ?? {}) as { code?: unknown; message?: unknown; status?: unknown };
  const code = typeof e.code === "number" ? e.code : typeof e.status === "number" ? e.status : undefined;
  const message = typeof e.message === "string" ? e.message : "";
  const upper = message.toUpperCase();
  // ADR-0036: the realtime server refused this tab's protocol version ("CLIENT_OUTDATED:<sentence>").
  if (upper.startsWith("CLIENT_OUTDATED")) return { state: "incompatible_client", message: CLIENT_OUTDATED_MESSAGE, retryable: false };
  if (upper.includes("BANNED")) return { state: "banned", message: "This account is restricted from Townsy.", retryable: false };
  if (upper.includes("FULL")) {
    return { state: "room_full", message: "This room is full right now. Wait a moment and try again.", retryable: true };
  }
  if (upper.includes("SUSPENDED")) {
    return { state: "room_suspended", message: "This room is temporarily closed.", retryable: false };
  }
  if (
    code === SDK_MATCHMAKE_CODES.AUTH_FAILED ||
    code === 401 ||
    upper.includes("TICKET") ||
    upper.includes("UNAUTHORIZED") ||
    upper.includes("AUTH")
  ) {
    return { state: "ticket_invalid", message: "Your room pass was not accepted. Requesting a new one…", retryable: true };
  }
  if (code === 403 || upper.includes("FORBIDDEN")) {
    return { state: "join_failed", message: message || "You are not allowed to enter this room.", retryable: false };
  }
  if (code === SDK_MATCHMAKE_CODES.MATCHMAKE_NO_HANDLER) {
    return { state: "join_failed", message: "The town is not accepting visitors right now.", retryable: true };
  }
  return {
    state: "join_failed",
    message: message ? `Couldn't join the room: ${message}` : "Couldn't join the room.",
    retryable: true,
  };
}
